import {
  Calendar,
  Circle,
  CircleDot,
  CirclePlay,
  CircleX,
  Eye,
  Navigation,
  Store,
  TrendingUp,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Col } from "../../../components/col";
import { Row } from "../../../components/row";
import ActivityLevelTable from "../components/ActivityLevelTable";
import AreaAnalysisTable from "../components/tables/AreaAnalysisTable";
import EventDetailTable from "../components/EventDetailTable";
import HeatMap from "../components/map/user-distribution/HeatMap";
import HeatMapPrintButton from "../components/map/shared/HeatMapPrintButton";
import LiveEventsMap from "../components/map/live-events/LiveEventsMap";
import MerchantMap from "../components/map/merchants/MerchantMap";
import MerchantTable from "../components/MerchantTable";
import { useAuthSession } from "../../auth/shared";
import { useOrganizationMapConfig } from "../hooks/useOrganizationMapConfig";
import { useHeatMapDashboard } from "../hooks/useHeatMapDashboard";

export default function HeatMapPdfReport() {
  return <HeatMapPdfReportContent />;
}

function HeatMapPdfReportContent() {
  const dashboard = useHeatMapDashboard(null);
  const { user } = useAuthSession();
  const mapConfig = useOrganizationMapConfig(user?.organization_id, user?.organization_map);
  const liveEvents = dashboard.liveEvents.data;
  const heatMap = dashboard.heatMapAreas.data;
  const targetOpportunities = dashboard.targetOpportunities.data;
  const statisticInfo = dashboard.statistics.data;
  const merchants = dashboard.merchants.data;
  const areaAnalysis = dashboard.areaAnalysis.data;
  const activityLevels = dashboard.liveEvents.activityLevels;
  const eventDetails = dashboard.liveEvents.eventDetails;
  const [pageReady, setPageReady] = useState<boolean>(false);

  useEffect(() => {
    if (
      liveEvents &&
      heatMap &&
      targetOpportunities &&
      statisticInfo &&
      merchants !== null &&
      areaAnalysis
    ) {
      setPageReady(true);
    }
  }, [liveEvents, heatMap, targetOpportunities, statisticInfo, merchants, areaAnalysis]);

  return (
    <>
      {!pageReady && (
        <div className="w-full h-[800px] flex items-center justify-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900"></div>
        </div>
      )}
      {pageReady && (
        <HeatMapPrintButton autoPrint={pageReady}>
          {/* --- FIRST SECTION: HEAT MAP --- */}
          <Row className="w-full flex items-center justify-center">
            <div className="w-[750px]">
              <div className="flex justify-between pb-5">
                <Col className="gap-0">
                  <span className="font-bold text-lg">Analytics Report</span>
                  <span className="text-xs text-[#4A5565] flex gap-1 items-center">
                    City of Fairburn
                    <Circle
                      size={4}
                      stroke="#4A5565"
                      fill="#4A5565"
                    ></Circle>
                    {new Date().toLocaleString("en-US", { month: "long", year: "numeric" })}
                  </span>
                </Col>
                {/* <Col className="pt-1">
                                <span className="text-xs text-[#4A5565]">{formatGeneratedDateTime()}</span>
                            </Col> */}
              </div>
              <Row className="py-2 bg-[#F9FAFB] text-[10px]">
                <Col className="w-1/4 gap-0">
                  <span className="text-center text-[12px] font-bold">
                    {statisticInfo?.total_users?.toLocaleString("en-US") ?? "-"}
                  </span>
                  <span className="text-center text-[#6A7282]">CivicPoints Users</span>
                  <span className="text-center text-[#00A63E]">
                    {statisticInfo?.adoption_rate.toFixed(2) ?? "-"}% adoption
                  </span>
                </Col>
                <Col className="w-1/4 gap-0">
                  <span className="text-center text-[12px] font-bold">
                    {statisticInfo?.total_citizens?.toLocaleString("en-US") ?? "-"}
                  </span>
                  <span className="text-center text-[#6A7282]">Total Citizens</span>
                  <span className="text-center text-[#6A7282]">Citi population</span>
                </Col>
                <Col className="w-1/4 gap-0">
                  <span className="text-center text-[12px] font-bold">
                    {statisticInfo?.target_audience?.toLocaleString("en-US") ?? "-"}
                  </span>
                  <span className="text-center text-[#6A7282]">Target Audience</span>
                  <span className="text-center text-[#6A7282]">Non-CivicPoints</span>
                </Col>
                <Col className="w-1/4 gap-0">
                  <span className="text-center text-[12px] font-bold">
                    {statisticInfo?.new_users_this_week.toLocaleString("en-US") ?? "-"}
                  </span>
                  <span className="text-center text-[#6A7282]">New Users This Week</span>
                  <span className="text-center text-[#00A63E]">New CivicPoints accounts</span>
                </Col>
              </Row>
              <hr className="mt-2 mb-3 border-[#EBEBEB]"></hr>
            </div>
          </Row>
          <div className="h-[700px] mx-[40px] my-[10px] border border-gray-200 dark:border-gray-700 rounded-2xl">
            <Row className="px-5 pt-5 pb-5 justify-between items-center">
              <Col className="gap-1 text-sm">
                <div className="flex">
                  <Navigation
                    className="mr-1"
                    color="#00C49A"
                    size={16}
                  />
                  <span className="text-gray-900 dark:text-gray-100">
                    Fairburn City - CivicPoints User Distribution
                  </span>
                </div>
                <div className="text-gray-600 dark:text-gray-400">
                  Interactive geographic distribution of CivicPoints users across city districts
                </div>
              </Col>
            </Row>
            <Row className="h-[600px] flex justify-center items-stretch">
              <div className="w-[750px] bg-white dark:bg-gray-800 flex flex-col">
                <div className="relative rounded-2xl flex-1">
                  {/* Adoption Levels legend */}
                  <div className="absolute top-6 right-6 bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-600 rounded-2xl px-4 py-3 text-sm z-[9999]">
                    <div className="font-semibold mb-2 flex items-center text-[#00BCD4] dark:text-cyan-400 text-[12px]">
                      <TrendingUp
                        size={16}
                        className="mr-1"
                      />
                      <span>Adoption Levels</span>
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-3 h-3 rounded-full bg-green-500"></span>
                      <span className="text-[12px] text-gray-700 dark:text-gray-300">
                        High (&gt;70%)
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                      <span className="text-[12px] text-gray-700 dark:text-gray-300">
                        Medium (50%-70%)
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-3 h-3 rounded-full bg-red-500"></span>
                      <span className="text-[12px] text-gray-700 dark:text-gray-300">
                        Low (&lt;50%)
                      </span>
                    </div>
                    <hr className="my-2 border-gray-200 dark:border-gray-600" />
                    <span className="text-[12px] text-gray-500 dark:text-gray-400">
                      Click markers for details
                    </span>
                  </div>

                  {/* City Info */}
                  <div className="absolute bottom-6 left-6 bg-white dark:bg-gray-800 shadow-md border-2 border-gray-200 dark:border-gray-600 rounded-xl px-2 py-1 text-[10px] z-[9999]">
                    <div className="flex justify-left items-center">
                      <Circle
                        className="mr-2"
                        size={12}
                        fill="#00BCD4"
                        stroke="#00BCD4"
                      />
                      <div>
                        <span className="text-[#00BCD4] dark:text-cyan-400 text-sm block">
                          City of Fairburn
                        </span>
                        <span className="block text-gray-700 dark:text-gray-300">
                          Fulton County, Georgia
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Map */}
                  <HeatMap areas={heatMap} mapConfig={mapConfig} />
                </div>
              </div>
            </Row>
          </div>

          <div className="min-h-[100px]"></div>

          {/* --- SECOND SECTION: LIVE EVENTS MAP --- */}
          <div className="h-[850px] mx-[40px] my-[10px] border border-gray-200 dark:border-gray-700 rounded-2xl">
            <Row className="px-5 pt-5 pb-5 justify-between items-center">
              <Col className="gap-1 text-sm">
                <div className="flex">
                  <Navigation
                    className="mr-1"
                    color="#00C49A"
                    size={16}
                  />
                  <span className="text-gray-900 dark:text-gray-100">
                    Fairburn City - Live Events Heatmap
                  </span>
                </div>
                <div className="text-gray-600 dark:text-gray-400">
                  Event activity across city districts - visual overview of citizen engagement
                </div>
              </Col>
            </Row>
            <Row className="h-[750px] flex justify-center items-stretch my-[10px]">
              <div className="w-[750px] bg-white dark:bg-gray-800 flex flex-col">
                <div className="relative flex-1">
                  {/* Event status legend */}
                  <div className="absolute top-6 right-6 bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-600 rounded-2xl px-4 py-3 text-[10px] z-[9999]">
                    <div className="font-semibold mb-2 flex items-center text-gray-900 dark:text-gray-100">
                      <Eye
                        size={12}
                        className="mr-1"
                      />
                      <span>All Events</span>
                    </div>
                    <div className="ml-2 mb-2 flex items-center text-gray-700 dark:text-gray-300">
                      <Calendar
                        size={12}
                        className="mr-2"
                      />
                      <span>8 events</span>
                    </div>
                    <div className="mt-2 mb-1 text-gray-700 dark:text-gray-300">Event Status:</div>
                    <div className="flex items-center gap-2 mb-1 text-gray-700 dark:text-gray-300">
                      <CircleDot
                        size={16}
                        fill="#00C853"
                        stroke="white"
                      />
                      <span>Active (6)</span>
                    </div>
                    <div className="flex items-center gap-2 mb-1 text-gray-700 dark:text-gray-300">
                      <CirclePlay
                        size={16}
                        fill="#00BCD4"
                        stroke="white"
                      />
                      <span>Starting Soon (1)</span>
                    </div>
                    <div className="flex items-center gap-2 mb-1 text-gray-700 dark:text-gray-300">
                      <CircleX
                        size={16}
                        fill="#FF9800"
                        stroke="white"
                      />
                      <span>Ending Soon (1)</span>
                    </div>
                    <hr className="my-2 border-gray-200 dark:border-gray-600" />
                    <span className="text-gray-500 dark:text-gray-400">
                      Click events for details
                    </span>
                  </div>

                  {/* City info */}
                  <div className="absolute bottom-6 left-6 bg-white dark:bg-gray-800 shadow-md border-2 border-gray-200 dark:border-gray-600 rounded-xl px-2 py-1 text-[10px] z-[9999]">
                    <div className="flex justify-left items-center">
                      <Circle
                        className="mr-2"
                        size={12}
                        fill="#00BCD4"
                        stroke="#00BCD4"
                      />
                      <div>
                        <span className="text-[#00BCD4] dark:text-cyan-400 text-sm block">
                          City of Fairburn
                        </span>
                        <span className="block text-gray-700 dark:text-gray-300">
                          Fulton County, Georgia
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Map */}
                  <LiveEventsMap areas={liveEvents} mapConfig={mapConfig} />
                </div>
              </div>
            </Row>
          </div>

          <div className="min-h-[100px]"></div>

          {/* --- THIRD SECTION: MERCHANT MAP --- */}
          <div className="h-[850px] mx-[40px] my-[10px] border border-gray-200 dark:border-gray-700 rounded-2xl">
            <Row className="px-5 pt-5 pb-5 justify-between items-center">
              <Col className="gap-1 text-sm">
                <div className="flex">
                  <Store
                    className="mr-1"
                    color="#00C49A"
                    size={16}
                  />
                  <span className="text-gray-900 dark:text-gray-100">
                    Fairburn City - Merchant Locations Map
                  </span>
                </div>
                <div className="text-gray-600 dark:text-gray-400">
                  Local business partners participating in coupon programs - visual distribution by
                  area
                </div>
              </Col>
            </Row>
            <Row className="h-[750px] flex justify-center items-stretch my-[10px]">
              <div className="w-[750px] bg-white dark:bg-gray-800 flex flex-col">
                <div className="relative flex-1">
                  {/* Merchant categories legend */}
                  <div className="absolute top-6 right-6 bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-600 rounded-[10px] px-3 py-3 z-[9999]">
                    <div className="font-semibold mb-2 text-gray-900 dark:text-gray-100 text-xs leading-4">
                      <span>Merchant Categories</span>
                    </div>
                    <div className="flex flex-col gap-1 mb-2">
                      {(() => {
                        // Get unique categories from merchants
                        const categoryMap = new Map<string, string>();
                        merchants?.forEach((merchant) => {
                          if (merchant.category?.name) {
                            const categoryName = merchant.category.name;
                            if (!categoryMap.has(categoryName)) {
                              const categoryColorMap: Record<string, string> = {
                                "Food & Beverage": "#ff6900",
                                "Health & Wellness": "#00c950",
                                Retail: "#ad46ff",
                                "Beauty & Personal care": "#f6339a",
                                Education: "#3b82f6",
                                Grocery: "#14b8a6",
                                Auto: "#ef4444",
                                Transport: "#f59e0b",
                                Travel: "#06b6d4",
                                Entertainment: "#3339f6",
                              };
                              categoryMap.set(
                                categoryName,
                                categoryColorMap[categoryName] || "#6a7282",
                              );
                            }
                          }
                        });
                        return Array.from(categoryMap.entries()).map(([name, color]) => (
                          <div
                            key={name}
                            className="flex gap-2 items-center"
                          >
                            <div
                              className="rounded-full shrink-0 w-3 h-3"
                              style={{ backgroundColor: color }}
                            />
                            <span className="text-xs text-gray-600 dark:text-gray-400 leading-4">
                              {name}
                            </span>
                          </div>
                        ));
                      })()}
                    </div>
                    <hr className="my-2 border-t border-gray-200 dark:border-gray-600"></hr>
                    <span className="text-xs text-gray-500 dark:text-gray-400 leading-4">
                      Click for details
                    </span>
                  </div>

                  {/* City info */}
                  <div className="absolute bottom-6 left-6 bg-white dark:bg-gray-800 shadow-md border-2 border-gray-200 dark:border-gray-600 rounded-xl px-2 py-1 text-[10px] z-[9999]">
                    <div className="flex justify-left items-center">
                      <Circle
                        className="mr-2"
                        size={12}
                        fill="#00BCD4"
                        stroke="#00BCD4"
                      />
                      <div>
                        <span className="text-[#00BCD4] dark:text-cyan-400 text-sm block">
                          City of Fairburn
                        </span>
                        <span className="block text-gray-700 dark:text-gray-300">
                          Fulton County, Georgia
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Map */}
                  <MerchantMap
                    merchants={merchants}
                    selectedMerchant={null}
                    mapConfig={mapConfig}
                  />
                </div>
              </div>
            </Row>
          </div>

          <div className="min-h-[100px]"></div>

          <Row className="w-full flex justify-center px-[20px] items-stretch">
            <ActivityLevelTable
              data={activityLevels}
              targetOpportunities={targetOpportunities}
              maxWidth="750px"
              scale={1}
            />
          </Row>

          <Row className="w-full flex justify-center px-[20px] items-stretch">
            <EventDetailTable
              data={eventDetails}
              maxWidth="750px"
              scale={1}
            />
          </Row>

          <Row className="w-full flex justify-center px-[20px] items-stretch">
            <MerchantTable
              data={merchants}
              maxWidth="750px"
              scale={1}
            />
          </Row>

          <Row className="w-full flex justify-center px-[20px] items-stretch">
            <AreaAnalysisTable
              data={areaAnalysis}
              maxWidth="750px"
              maxHeight="3500px"
              scale={1}
            />
          </Row>
        </HeatMapPrintButton>
      )}
    </>
  );
}
